/**
 * Meta Ads fase 1 (conectar Meta, vincular anuncios con unidades, ver resultados).
 * Contrato: argenlotapi `docs/frontend/FRONTEND_META_ADS.md`. Ver docs/maps/meta-ads.md.
 *
 * `ownerUserId` (espacio ajeno, editor de Espacio): lecturas → `?owner_user_id=`,
 * vínculos → `owner_user_id` en el body. Conectar / desconectar / elegir cuentas /
 * URL de login / sincronizar son **solo del dueño**: nunca llevan owner.
 *
 * Todas devuelven el envelope de `httpClient` (`_httpOk` / `_httpStatus`).
 * Un fallo de red o timeout vuelve como `{ _httpOk: false, _httpStatus: null }`;
 * el `AbortError` del consumidor se propaga. Los 429 de Meta son semánticos
 * (`META_SYNC_THROTTLED` / `META_RATE_LIMITED`): nunca se reintentan solos.
 */
import { API_BASE_URL } from '../Utils/constants';
import { authFetch } from './authFetch';
import { get, post } from './httpClient';
import { spaceOwnerQueryParams } from './spaceMembersService';
import { META_NETWORK_MESSAGE, UNIT_REPORT_SHARE_DEFAULT_DAYS } from '../MetaAds/metaAdsConfig';

/** Períodos rápidos que acepta la API (`days`). */
export const META_PERIOD_DAYS = [7, 30, 90];
export const META_DEFAULT_PERIOD_DAYS = 30;
/** Ventanas de `POST /syncmetanow`. */
export const META_SYNC_WINDOWS = [7, 28];
/** Tope de registros por request de vínculos y de `records=` en métricas. */
export const META_MAX_RECORDS = 50;
/** Tope de cuentas seguidas en `POST /updatemetaadaccounts`. */
export const META_MAX_AD_ACCOUNTS = 20;
export const META_ITEM_KINDS = ['user_lot', 'cadastral_lot', 'cad_parcel'];
// `geo` = country + region. Sin `breakdown` la API manda los 4 tipos (pesado: pedir cada tipo por separado).
export const META_BREAKDOWNS = ['age_gender', 'platform_position', 'country', 'region', 'geo'];

/**
 * La sync manual tarda unos segundos y la Lambda tiene ≥ 30 s: más que los 20 s
 * por defecto del cliente.
 */
const SYNC_TIMEOUT_MS = 35000;

const NETWORK_ERROR = Object.freeze({ _httpOk: false, _httpStatus: null, error: META_NETWORK_MESSAGE });

async function safeRequest(run) {
  try {
    return await run();
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { ...NETWORK_ERROR };
  }
}

const trimId = (value) => (value == null ? '' : String(value).trim());

/**
 * Query del período: `days` (7 | 30 | 90) o `since` + `until` (`YYYY-MM-DD`, juntos).
 * Sin nada → la API usa 30 días.
 *
 * @param {{ days?: number, since?: string, until?: string }} [period]
 * @returns {{ days?: number, since?: string, until?: string }}
 */
export function buildMetaPeriodParams(period = {}) {
  const since = trimId(period?.since);
  const until = trimId(period?.until);
  if (since && until) return { since, until };
  const days = Number(period?.days);
  return META_PERIOD_DAYS.includes(days) ? { days } : {};
}

/**
 * Normaliza registros a `{ item_kind, id }` (acepta `itemKind` del Front).
 * Descarta los que no tienen tipo válido o id.
 *
 * @param {Array<{ item_kind?: string, itemKind?: string, id: string }>} records
 * @returns {Array<{ item_kind: string, id: string }>}
 */
export function toMetaRecordRefs(records) {
  if (!Array.isArray(records)) return [];
  return records
    .map((r) => ({ item_kind: trimId(r?.item_kind ?? r?.itemKind), id: trimId(r?.id) }))
    .filter((r) => META_ITEM_KINDS.includes(r.item_kind) && r.id);
}

/**
 * `records=user_lot:<uuid>,cad_parcel:<uuid>` de `GET /getcampaignmetrics`.
 *
 * @param {Array<{ item_kind?: string, itemKind?: string, id: string }>} records
 * @returns {string}
 */
export function serializeMetaRecords(records) {
  return toMetaRecordRefs(records).map((r) => `${r.item_kind}:${r.id}`).join(',');
}

const ownerBody = (ownerUserId) => spaceOwnerQueryParams(ownerUserId);

// ── Conexión y checklist ──────────────────────────────────────────────────

/**
 * GET /getmetaconnection — estado de la conexión + checklist. Sin conexión
 * también es 200 (`status: "not_connected"`). 404 sin `code` = flag apagado.
 *
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getMetaConnection({ ownerUserId, signal } = {}) {
  return safeRequest(() => get('/getmetaconnection', { params: spaceOwnerQueryParams(ownerUserId), signal }));
}

/**
 * GET /getmetaauthurl (solo dueño) — `{ auth_url, state, state_expires_at,
 * redirect_uri, mode }` (+ `fixture_code` en modo demo; `auth_url` puede ser
 * `null` en demo).
 *
 * @param {{ redirectUri?: string|null, signal?: AbortSignal }} [options]
 *   `redirectUri`: solo fuera del dominio por defecto (localhost); tiene que
 *   estar en la lista del entorno o la API responde 400 `VALIDATION_ERROR`
 *   (`field: "redirect_uri"`).
 */
export function getMetaAuthUrl({ redirectUri = null, access = null, signal } = {}) {
  // F2a: `access=write` pide el login «con escritura» (ads_management). Sin él, todo igual que antes.
  const params = {
    ...(redirectUri ? { redirect_uri: redirectUri } : {}),
    ...(access === 'write' ? { access: 'write' } : {}),
  };
  const hasParams = Object.keys(params).length > 0;
  return safeRequest(() => get('/getmetaauthurl', { ...(hasParams ? { params } : {}), signal }));
}

/**
 * POST /connectmeta (solo dueño) — canjea el `code` del login de Meta.
 * Devuelve el `data` de `getmetaconnection` + `warnings`.
 *
 * @param {{ code: string, state: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 */
export function connectMeta({ code, state } = {}, { signal } = {}) {
  return safeRequest(() => post('/connectmeta', { body: { code: trimId(code), state: trimId(state) }, signal }));
}

/**
 * POST /disconnectmeta (solo dueño). Idempotente.
 *
 * @param {{ purgeData?: boolean }} [body] `purgeData` borra además cuentas, campañas, vínculos y métricas.
 * @param {{ signal?: AbortSignal }} [options]
 */
export function disconnectMeta({ purgeData = false } = {}, { signal } = {}) {
  // Sin purge el body va vacío (`purge_data` es opcional y por defecto false): se conserva el historial.
  return safeRequest(() => post('/disconnectmeta', { body: purgeData === true ? { purge_data: true } : {}, signal }));
}

// ── Cuentas publicitarias ─────────────────────────────────────────────────

/**
 * GET /getmetaadaccounts — lo guardado; `refresh` (solo dueño) las vuelve a pedir a Meta.
 *
 * @param {{ ownerUserId?: string|null, refresh?: boolean, signal?: AbortSignal }} [options]
 */
export function getMetaAdAccounts({ ownerUserId, refresh = false, signal } = {}) {
  const params = { ...spaceOwnerQueryParams(ownerUserId) };
  if (refresh) params.refresh = 1;
  return safeRequest(() => get('/getmetaadaccounts', { params, signal }));
}

/**
 * POST /updatemetaadaccounts (solo dueño) — **exactamente** las cuentas que se
 * siguen (`[]` = ninguna, máx. 20). `initial_load_pending` → llamar a `syncMetaNow`.
 *
 * @param {string[]} adAccountIds
 * @param {{ signal?: AbortSignal }} [options]
 */
export function updateMetaAdAccounts(adAccountIds, { signal } = {}) {
  const ids = Array.isArray(adAccountIds) ? adAccountIds.map(trimId).filter(Boolean) : [];
  return safeRequest(() => post('/updatemetaadaccounts', { body: { ad_account_ids: ids }, signal }));
}

/**
 * POST /syncmetanow (solo dueño). Una vez por hora (`META_SYNC_THROTTLED` con
 * `retry_after_s`); no se reintenta ante 429.
 *
 * @param {{ windowDays?: 7|28 }} [body]
 * @param {{ signal?: AbortSignal }} [options]
 */
export function syncMetaNow({ windowDays } = {}, { signal } = {}) {
  const body = META_SYNC_WINDOWS.includes(Number(windowDays)) ? { window_days: Number(windowDays) } : {};
  return safeRequest(() => post('/syncmetanow', {
    body, signal, timeoutMs: SYNC_TIMEOUT_MS, retryOn429: 0,
  }));
}

// ── Campañas y vínculos ───────────────────────────────────────────────────

/**
 * GET /getmetacampaigns — campañas con sus anuncios y estado de vínculo.
 *
 * @param {{ period?: object, includeInactive?: boolean, ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getMetaCampaigns({ period, includeInactive = false, ownerUserId, signal } = {}) {
  const params = { ...buildMetaPeriodParams(period), ...spaceOwnerQueryParams(ownerUserId) };
  if (includeInactive) params.include_inactive = 1;
  return safeRequest(() => get('/getmetacampaigns', { params, signal }));
}

/**
 * GET /getmetaadpreview (dueño o editor, ajuste 12) — vista previa real del anuncio (llama a
 * Meta en el momento). `format` opcional (default de la API: MOBILE_FEED_STANDARD). La URL
 * vence: pedirla cada vez que se abre, no guardarla.
 *
 * @param {{ adId: string, format?: string|null, ownerUserId?: string|null, signal?: AbortSignal }} input
 */
export function getMetaAdPreview({ adId, format = null, ownerUserId, signal } = {}) {
  const params = { ad_id: trimId(adId), ...spaceOwnerQueryParams(ownerUserId) };
  if (format) params.format = format;
  return safeRequest(() => get('/getmetaadpreview', { params, signal }));
}

/** Envelope local (sin request) cuando un unlink llega sin registros y sin `all: true`. */
const missingUnlinkRecords = () => Promise.resolve({
  _httpOk: false,
  _httpStatus: 400,
  code: 'VALIDATION_ERROR',
  field: 'records',
  error: 'Elegí qué unidades desvincular.',
});

const linkRequest = (path, idKey, id, records, ownerUserId, signal, { requireRecords, all = false }) => {
  const refs = toMetaRecordRefs(records);
  // Sin `records` la API desvincula **todo**: solo con `all: true` explícito (tras confirmar).
  if (!requireRecords && !refs.length && all !== true) return missingUnlinkRecords();
  const body = { [idKey]: trimId(id), ...ownerBody(ownerUserId) };
  if (requireRecords || (refs.length && all !== true)) body.records = refs;
  return safeRequest(() => post(path, { body, signal }));
};

/**
 * POST /linkadunit (dueño o editor) — vincula un anuncio con unidades (máx. 50, idempotente).
 *
 * @param {{ adId: string, records: object[], ownerUserId?: string|null }} input
 * @param {{ signal?: AbortSignal }} [options]
 */
export function linkAdUnit({ adId, records, ownerUserId } = {}, { signal } = {}) {
  return linkRequest('/linkadunit', 'ad_id', adId, records, ownerUserId, signal, { requireRecords: true });
}

/**
 * POST /unlinkadunit (dueño o editor). Con `records`, solo esas unidades; para
 * desvincular el anuncio de **todas** hay que pasar `all: true` (manda el body
 * sin `records`). Sin ninguno de los dos no llama a la API (400 local).
 *
 * @param {{ adId: string, records?: object[], all?: boolean, ownerUserId?: string|null }} input
 * @param {{ signal?: AbortSignal }} [options]
 */
export function unlinkAdUnit({ adId, records, all = false, ownerUserId } = {}, { signal } = {}) {
  return linkRequest('/unlinkadunit', 'ad_id', adId, records, ownerUserId, signal, { requireRecords: false, all });
}

/**
 * POST /linkcampaignunit (dueño o editor) — atajo: vincula todos los anuncios que la campaña tiene hoy.
 *
 * @param {{ campaignId: string, records: object[], ownerUserId?: string|null }} input
 * @param {{ signal?: AbortSignal }} [options]
 */
export function linkCampaignUnit({ campaignId, records, ownerUserId } = {}, { signal } = {}) {
  return linkRequest('/linkcampaignunit', 'campaign_id', campaignId, records, ownerUserId, signal, { requireRecords: true });
}

/**
 * POST /unlinkcampaignunit (dueño o editor). Con `records`, esas unidades en
 * todos los anuncios de la campaña; `all: true` borra **todos** sus vínculos.
 * Sin ninguno de los dos no llama a la API (400 local).
 *
 * @param {{ campaignId: string, records?: object[], all?: boolean, ownerUserId?: string|null }} input
 * @param {{ signal?: AbortSignal }} [options]
 */
export function unlinkCampaignUnit({ campaignId, records, all = false, ownerUserId } = {}, { signal } = {}) {
  return linkRequest('/unlinkcampaignunit', 'campaign_id', campaignId, records, ownerUserId, signal, { requireRecords: false, all });
}

// ── Métricas ──────────────────────────────────────────────────────────────

/**
 * GET /getcampaignmetrics — un solo filtro (o ninguno = cuenta): `campaignId`,
 * `adId` o `records` (máx. 50; cada anuncio cuenta una vez). Si llegan varios,
 * gana el primero en ese orden (la API rechaza dos a la vez).
 *
 * @param {{ period?: object, campaignId?: string, adId?: string, records?: object[],
 *   ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getCampaignMetrics({ period, campaignId, adId, records, ownerUserId, signal } = {}) {
  const params = { ...buildMetaPeriodParams(period), ...spaceOwnerQueryParams(ownerUserId) };
  const recordsParam = serializeMetaRecords(records);
  if (trimId(campaignId)) params.campaign_id = trimId(campaignId);
  else if (trimId(adId)) params.ad_id = trimId(adId);
  else if (recordsParam) params.records = recordsParam;
  return safeRequest(() => get('/getcampaignmetrics', { params, signal }));
}

/**
 * GET /getcampaignbreakdowns — edad/género, plataforma/ubicación, país y región (informativo).
 * Sin `campaignId` suma todas las campañas de las cuentas seguidas. No respeta `include_inactive`.
 *
 * @param {{ period?: object, campaignId?: string,
 *   breakdown?: 'age_gender'|'platform_position'|'country'|'region'|'geo',
 *   ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getCampaignBreakdowns({ period, campaignId, breakdown, ownerUserId, signal } = {}) {
  const params = { ...buildMetaPeriodParams(period), ...spaceOwnerQueryParams(ownerUserId) };
  if (trimId(campaignId)) params.campaign_id = trimId(campaignId);
  if (META_BREAKDOWNS.includes(breakdown)) params.breakdown = breakdown;
  return safeRequest(() => get('/getcampaignbreakdowns', { params, signal }));
}

/**
 * GET /getmetaadsets — conjuntos de anuncios con su presupuesto, estado y público CONFIGURADO
 * por el anunciante (`targeting`). Sin paginación ni límite: pedirlo con `campaignId` siempre
 * que se pueda. 404 sin `code` / 503 `META_ADS_NOT_MIGRATED` = «no disponible» (ver
 * `isMetaFase1Unavailable`).
 *
 * @param {{ period?: object, campaignId?: string, includeInactive?: boolean,
 *   ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getMetaAdSets({ period, campaignId, includeInactive = false, ownerUserId, signal } = {}) {
  const params = { ...buildMetaPeriodParams(period), ...spaceOwnerQueryParams(ownerUserId) };
  if (trimId(campaignId)) params.campaign_id = trimId(campaignId);
  if (includeInactive) params.include_inactive = 1;
  return safeRequest(() => get('/getmetaadsets', { params, signal }));
}

/**
 * GET /getunitcampaigns — anuncios vinculados a una unidad (pestaña Campañas de la ficha).
 *
 * @param {{ itemKind: string, id: string, period?: object, ownerUserId?: string|null, signal?: AbortSignal }} input
 */
export function getUnitCampaigns({ itemKind, id, period, ownerUserId, signal } = {}) {
  const params = {
    item_kind: trimId(itemKind),
    id: trimId(id),
    ...buildMetaPeriodParams(period),
    ...spaceOwnerQueryParams(ownerUserId),
  };
  return safeRequest(() => get('/getunitcampaigns', { params, signal }));
}

/**
 * GET /getmetaresults — resultados de **todo el espacio** (`scope=space`, sin `records`): totales y
 * una fila por unidad. No acepta `campaign_id` (lo ignora) ni se usa para el informe del propietario
 * (eso es `getUnitReport`).
 *
 * @param {{ period?: object, includeInactive?: boolean, ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getMetaResults({ period, includeInactive = false, ownerUserId, signal } = {}) {
  const params = { ...buildMetaPeriodParams(period), ...spaceOwnerQueryParams(ownerUserId) };
  if (includeInactive) params.include_inactive = 1;
  return safeRequest(() => get('/getmetaresults', { params, signal }));
}

// ── Descarga CSV de getmetaresults (tanda 4) ─────────────────────────────────

/** Filas que sabe exportar `getmetaresults?format=csv`. */
export const META_RESULTS_CSV_ROWS = ['units', 'ads', 'campaigns'];

/** `Content-Disposition: attachment; filename="x.csv"` → `x.csv` (sin rutas ni comillas). */
export function parseCsvFilename(header) {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(String(header || ''));
  if (!match) return null;
  let name = match[1].trim();
  try { name = decodeURIComponent(name); } catch { /* queda tal cual */ }
  name = [...name].filter((c) => c.charCodeAt(0) >= 32 && !'\\/:*?"<>|'.includes(c)).join('').replace(/^\.+/, '').trim();
  return name || null;
}

/**
 * GET /getmetaresults?format=csv — descarga las filas de `units` | `ads` | `campaigns` del espacio.
 * No es JSON y lleva JWT: `fetch` con Bearer (`authFetch`) y `blob()`. El nombre del archivo es el
 * que manda la API (`terrio-meta-<rows>-<desde>_<hasta>.csv`); si el navegador no deja leer
 * `Content-Disposition` (CORS) se arma con el mismo formato. Separador `;` y títulos en español
 * (lo que abre bien Excel es-AR). El editor manda `owner_user_id`.
 *
 * Éxito → `{ _httpOk: true, _httpStatus: 200, blob, filename }`. Falla → envelope de error
 * (`code` si la API lo mandó; `_httpStatus: null` si no hubo red). El `AbortError` se propaga.
 *
 * @param {{ rows: 'units'|'ads'|'campaigns', period?: object, includeInactive?: boolean,
 *   ownerUserId?: string|null, signal?: AbortSignal }} input
 */
export async function downloadMetaResultsCsv({ rows, period, includeInactive = false, ownerUserId, signal } = {}) {
  if (!META_RESULTS_CSV_ROWS.includes(rows)) return { _httpOk: false, _httpStatus: 400, code: 'VALIDATION_ERROR', error: 'Tipo de descarga inválido.' };
  const params = {
    ...buildMetaPeriodParams(period),
    ...spaceOwnerQueryParams(ownerUserId),
    format: 'csv',
    rows,
    csv_sep: 'semicolon',
    csv_header: 'es',
  };
  if (includeInactive) params.include_inactive = 1;
  const query = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString();
  let response;
  try {
    response = await authFetch(`${API_BASE_URL}/getmetaresults?${query}`, { method: 'GET', headers: { Accept: 'text/csv' }, signal });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { ...NETWORK_ERROR };
  }
  // El éxito es `text/csv`; todo lo demás (error o un JSON inesperado) se lee como JSON para usar su `code`.
  const contentType = String(response.headers?.get?.('Content-Type') || '').toLowerCase();
  const isCsv = contentType.includes('text/csv');
  if (!response.ok || (contentType && !isCsv)) {
    let body = {};
    try {
      const parsed = await response.json();
      body = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch { body = {}; }
    return { ...body, _httpOk: false, _httpStatus: response.ok ? 502 : response.status };
  }
  let blob;
  try {
    blob = await response.blob();
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { ...NETWORK_ERROR };
  }
  const since = trimId(period?.since);
  const until = trimId(period?.until);
  const fallback = since && until ? `terrio-meta-${rows}-${since}_${until}.csv` : `terrio-meta-${rows}.csv`;
  return {
    _httpOk: true,
    _httpStatus: response.status,
    blob,
    filename: parseCsvFilename(response.headers?.get?.('Content-Disposition')) || fallback,
  };
}

// ── Informe por propiedad (para el propietario) ──────────────────────────────
// Contrato: argenlotapi `docs/frontend/FRONTEND_INFORME_PROPIEDAD.md`. El gasto y los costos
// van **siempre incluidos** (decisión de producto): este servicio no manda nunca `include_spend`.

/**
 * GET /getunitreport (dueño o editor) — el informe en JSON de una propiedad.
 * 404 sin `code` / red / 503 `META_ADS_NOT_MIGRATED` = «no disponible» (`isMetaFase1Unavailable`).
 *
 * @param {{ itemKind: string, id: string, period?: object, ownerUserId?: string|null, signal?: AbortSignal }} input
 */
export function getUnitReport({ itemKind, id, period, ownerUserId, signal } = {}) {
  const params = {
    item_kind: trimId(itemKind),
    id: trimId(id),
    ...buildMetaPeriodParams(period),
    ...spaceOwnerQueryParams(ownerUserId),
  };
  return safeRequest(() => get('/getunitreport', { params, signal }));
}

/**
 * POST /createunitreportshare (dueño o editor) — crea un link con vencimiento. El período del
 * informe queda congelado en el link. Responde 201 con el `token` **una sola vez**.
 *
 * @param {{ itemKind: string, id: string, period?: object, expiresInDays?: number,
 *   ownerUserId?: string|null, signal?: AbortSignal }} input
 */
export function createUnitReportShare({
  itemKind, id, period, expiresInDays = UNIT_REPORT_SHARE_DEFAULT_DAYS, ownerUserId, signal,
} = {}) {
  const body = {
    item_kind: trimId(itemKind),
    id: trimId(id),
    ...buildMetaPeriodParams(period),
    expires_in_days: expiresInDays,
    ...ownerBody(ownerUserId),
  };
  return safeRequest(() => post('/createunitreportshare', { body, signal }));
}

/**
 * GET /getunitreportshares (dueño o editor) — los links de una propiedad (hasta 50, sin `token`).
 *
 * @param {{ itemKind: string, id: string, ownerUserId?: string|null, signal?: AbortSignal }} input
 */
export function getUnitReportShares({ itemKind, id, ownerUserId, signal } = {}) {
  const params = { item_kind: trimId(itemKind), id: trimId(id), ...spaceOwnerQueryParams(ownerUserId) };
  return safeRequest(() => get('/getunitreportshares', { params, signal }));
}

/**
 * POST /revokeunitreportshare (dueño o editor) — revoca un link (idempotente).
 *
 * @param {{ shareId: string, ownerUserId?: string|null, signal?: AbortSignal }} input
 */
export function revokeUnitReportShare({ shareId, ownerUserId, signal } = {}) {
  const body = { share_id: trimId(shareId), ...ownerBody(ownerUserId) };
  return safeRequest(() => post('/revokeunitreportshare', { body, signal }));
}

/**
 * GET /getsharedunitreport — **pública, sin JWT** (`auth: false`: no manda `Authorization` aunque
 * haya sesión). Link inexistente / vencido / revocado → 404 `UNIT_REPORT_LINK_UNAVAILABLE`;
 * 429 `UNIT_REPORT_RATE_LIMITED` (reintentar en 1 minuto). El token es una credencial: va solo en
 * la query de este pedido, nunca se loguea ni se manda a analytics.
 *
 * @param {{ token: string, signal?: AbortSignal }} input
 */
export function getSharedUnitReport({ token, signal } = {}) {
  return safeRequest(() => get('/getsharedunitreport', { params: { token: trimId(token) }, auth: false, signal }));
}

// ── Acciones en Meta (F2a, FRONTEND_META_ESCRITURA.md) ───────────────────

/**
 * GET /getmetawritesettings (dueño o editor; el editor solo lee) — estado de la escritura por cuenta.
 *
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export function getMetaWriteSettings({ ownerUserId, signal } = {}) {
  return safeRequest(() => get('/getmetawritesettings', { params: spaceOwnerQueryParams(ownerUserId), signal }));
}

/**
 * POST /updatemetawritesettings (solo dueño). Habilitar exige `confirm: true`; deshabilitar no.
 *
 * @param {{ adAccountId: string, enabled: boolean }} body
 * @param {{ signal?: AbortSignal }} [options]
 */
export function updateMetaWriteSettings({ adAccountId, enabled } = {}, { signal } = {}) {
  const body = { ad_account_id: trimId(adAccountId), enabled: enabled === true };
  if (enabled === true) body.confirm = true;
  return safeRequest(() => post('/updatemetawritesettings', { body, signal, retryOn429: 0 }));
}

/** Niveles con acciones en el Front (F2a + F2b: campaña, conjunto y anuncio). */
export const META_WRITE_LEVELS = {
  campaign: { path: '/updatemetacampaign', idKey: 'campaign_id' },
  adset: { path: '/updatemetaadset', idKey: 'adset_id' },
  ad: { path: '/updatemetaad', idKey: 'ad_id' },
};
export const META_WRITE_BUDGET_ACTIONS = ['set_daily_budget', 'set_lifetime_budget'];
export const META_WRITE_ACTIONS = ['pause', 'resume', 'rename', ...META_WRITE_BUDGET_ACTIONS, 'set_end_time', 'archive', 'delete'];
/** El anuncio no tiene presupuesto ni fecha propios (§3.4). */
const AD_ACTIONS = ['pause', 'resume', 'rename', 'archive', 'delete'];
/** Palabra exacta que pide `delete` al confirmar (§3.5). */
export const META_DELETE_CONFIRM_TEXT = 'ELIMINAR';

/**
 * POST /updatemetacampaign | /updatemetaadset | /updatemetaad (solo dueño): una acción por pedido.
 * `dryRun: true` → vista previa (no cambia nada). Confirmar: sin `dryRun`, con `expected`
 * (copiado del preview) e `idempotencyKey` (uuid). Nunca se reintenta solo (ni ante 429).
 * - `value`: nombre (`rename`), monto en unidad mayor (`set_*_budget`, con `currency`) o `YYYY-MM-DD`
 *   (`set_end_time`, 23:59 en la zona de la cuenta).
 * - `delete` al confirmar manda `confirm_text` (tiene que ser «ELIMINAR»; el preview no lo pide).
 *
 * @param {{ level: 'campaign'|'adset'|'ad', id: string, action: string, value?: string|number,
 *   currency?: string|null, confirmText?: string|null, dryRun?: boolean, expected?: object|null,
 *   idempotencyKey?: string|null }} input
 * @param {{ signal?: AbortSignal }} [options]
 */
export function updateMetaObject({
  level, id, action, value, currency = null, confirmText = null, dryRun = false, expected = null, idempotencyKey = null,
} = {}, { signal } = {}) {
  const target = META_WRITE_LEVELS[level];
  if (!target || !META_WRITE_ACTIONS.includes(action) || (level === 'ad' && !AD_ACTIONS.includes(action))) {
    return Promise.resolve({ _httpOk: false, _httpStatus: 400, code: 'VALIDATION_ERROR', field: 'action', error: 'Esa acción no está disponible.' });
  }
  const body = { [target.idKey]: trimId(id), action };
  if (action === 'rename' || action === 'set_end_time') body.value = typeof value === 'string' ? value.trim() : '';
  if (META_WRITE_BUDGET_ACTIONS.includes(action)) {
    body.value = typeof value === 'number' ? value : String(value ?? '').trim();
    body.currency = typeof currency === 'string' ? currency.trim().toUpperCase() : '';
  }
  if (dryRun) {
    body.dry_run = true;
  } else {
    body.expected = expected || {};
    body.idempotency_key = trimId(idempotencyKey);
    if (action === 'delete') body.confirm_text = typeof confirmText === 'string' ? confirmText : '';
  }
  return safeRequest(() => post(target.path, { body, signal, retryOn429: 0 }));
}

/**
 * GET /getmetawritelog (solo el dueño; T9, §3.7) — historial «Cambios hechos desde Terrio».
 * Lo más nuevo primero; `cursor` = `next_cursor` de la página anterior (con los mismos filtros).
 * Fechas `YYYY-MM-DD` (días de Córdoba). Sin `owner_user_id`: cada uno lee el suyo.
 *
 * @param {{ adAccountId?: string, campaignId?: string, objectId?: string, level?: string, from?: string,
 *   to?: string, limit?: number, cursor?: string|null, signal?: AbortSignal }} [options]
 */
export function getMetaWriteLog({
  adAccountId, campaignId, objectId, level, from, to, limit, cursor, signal,
} = {}) {
  const params = {};
  if (trimId(adAccountId)) params.ad_account_id = trimId(adAccountId);
  if (trimId(campaignId)) params.campaign_id = trimId(campaignId);
  if (trimId(objectId)) params.object_id = trimId(objectId);
  if (['campaign', 'adset', 'ad'].includes(level)) params.level = level;
  if (/^\d{4}-\d{2}-\d{2}$/.test(from || '')) params.from = from;
  if (/^\d{4}-\d{2}-\d{2}$/.test(to || '')) params.to = to;
  if (Number.isFinite(limit) && limit > 0) params.limit = Math.min(100, Math.trunc(limit));
  if (trimId(cursor)) params.cursor = trimId(cursor);
  return safeRequest(() => get('/getmetawritelog', { params, signal }));
}

// ── Crear campañas (F2b API, FRONTEND_META_CREAR.md) ─────────────────────

/** Registros con los que se puede crear (§4.0): unidad y parcela del showroom; catastro no. */
export const META_CREATE_ITEM_KINDS = ['user_lot', 'cad_parcel'];

/**
 * GET /getmetacreateoptions (solo el dueño; §4.1) — datos y valores por defecto del formulario.
 * Solo lectura (no crea nada en Meta). Sin `owner_user_id`: un espacio ajeno da 403 META_OWNER_ONLY.
 * `adAccountId` opcional: al cambiar de cuenta se vuelve a pedir con ella.
 *
 * @param {{ itemKind: 'user_lot'|'cad_parcel', id: string, adAccountId?: string|null, signal?: AbortSignal }} options
 */
export function getMetaCreateOptions({ itemKind, id, adAccountId = null, signal } = {}) {
  const params = { item_kind: trimId(itemKind), id: trimId(id) };
  if (trimId(adAccountId)) params.ad_account_id = trimId(adAccountId);
  return safeRequest(() => get('/getmetacreateoptions', { params, signal }));
}

/**
 * POST /createmetacampaign (solo el dueño; §4.2–4.3). `dryRun: true` → vista previa (no crea nada).
 * Confirmar: EXACTAMENTE el mismo `body` del preview, sin `dry_run`, con `expected` (texto del preview)
 * e `idempotencyKey` (uuid). Nunca se reintenta solo (ni ante 429).
 *
 * @param {{ body: object, dryRun?: boolean, expected?: string|null, idempotencyKey?: string|null }} input
 * @param {{ signal?: AbortSignal }} [options]
 */
export function createMetaCampaign({ body, dryRun = false, expected = null, idempotencyKey = null } = {}, { signal } = {}) {
  const payload = { ...(body && typeof body === 'object' ? body : {}) };
  delete payload.dry_run;
  delete payload.expected;
  delete payload.idempotency_key;
  if (dryRun) {
    payload.dry_run = true;
  } else {
    payload.expected = typeof expected === 'string' ? expected : '';
    payload.idempotency_key = trimId(idempotencyKey);
  }
  return safeRequest(() => post('/createmetacampaign', { body: payload, signal, retryOn429: 0 }));
}
